// copy from reducerify
// https://github.com/wooandoo/reducerify/blob/main/src/pure.ts

import type { ActionCreatorMap, ActionUnion, ActionWithPayload } from './reducerify-types.ts';

type Handler<TState> = (state: TState, action: ActionWithPayload<any>) => TState;

// Definition of the handlers map: either a state-only reducer or state+action reducer
export type ReducerHandlers<TState> = {
  [TActionType in string]: Handler<TState>;
};

type CreateReducerResult<THandlers extends ReducerHandlers<TState>, TState> = {
  reducer: (state: TState, action: ActionUnion<THandlers, TState>) => TState;
  actions: ActionCreatorMap<THandlers, TState>;
};

// 5. Implementation of createReducer that infers everything
export function forState<TState>() {
  return {
    createReducer: <THandlers extends ReducerHandlers<TState>>(
      handlers: THandlers,
    ): CreateReducerResult<THandlers, TState> => {
      const actions = {} as ActionCreatorMap<THandlers, TState>;

      // Generate action creators based on handlerFunction.length
      for (const actionType in handlers) {
        const handlerFunction = handlers[actionType];
        if (handlerFunction === undefined) {
          throw new Error(`handlerFunction is missing for actionType ${actionType}`);
        }

        if (handlerFunction.length > 1) {
          // Action with payload
          actions[actionType as keyof THandlers] = ((payload: any) => ({
            type: actionType,
            payload,
          })) as any;
        } else {
          // Action without payload
          actions[actionType as keyof THandlers] = (() => ({
            type: actionType,
          })) as any;
        }
      }

      // Reducer implementation remains mostly unchanged
      const reducer = (state: TState, action: ActionUnion<THandlers, TState>): TState => {
        // Check if the action type exists in the handlers
        if (!(action.type in handlers)) {
          return state;
        }

        const handler = handlers[action.type as keyof THandlers];

        if (handler === undefined) {
          throw new Error(`handler is missing for action type ${action.type}`);
        }

        // If handler expects an action param
        if (handler.length > 1) {
          return (handler as (state: TState, action: ActionWithPayload<any>) => TState)(
            state,
            action as ActionWithPayload<any>,
          );
        }

        // For handlers without payload, we need to ensure proper typing
        return (handler as (state: TState, action?: any) => TState)(state);
      };

      return { reducer, actions };
    },
  };
}
